import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { isModalOpen, setToastMessage } from '../redux/FlowReducer';
import { Box, Typography, Select, MenuItem, FormControl, InputLabel, Card, CardContent } from '@mui/material';
import VariableAutocomplete from '../VariableAutocomplete';
import PanelShell from './PanelShell';

const MetinselIslemlerPanel = ({ nodeType, nodeId, data, onChange }) => {
  const dispatch = useDispatch();
  const vars = useSelector((state) => state.flow.vars) || [];
  const tags = useSelector((state) => state.flow.tags) || [];
  const localVars = useSelector((state) => state.flow.localVars) || [];

  const [focusedInput, setFocusedInput] = useState(null);
  const [parameterMappings, setParameterMappings] = useState({});
  const [operation, setOperation] = useState(data?.operation || 'concat');

  // Parse variable string to get proper variable object
  const parseVariableString = (value, inputType) => {
    if (!value) return null;

    if (inputType === 'text' || inputType === 'constant') {
      return {
        variableId: value,
        variableName: value,
        variableType: 'constant'
      };
    }

    // Check for different variable types
    if (value.startsWith('lv_')) {
      const varName = value.substring(3);
      const foundLocalVar = localVars.find(v => v.name === varName);
      return {
        variableId: value,
        variableName: foundLocalVar ? foundLocalVar.name : value,
        variableType: 'LV'
      };
    }

    if (value.startsWith('var_')) {
      const varId = value.substring(4);
      const foundVar = vars.find(v => {
        const varIdValue = v.varId !== undefined ? v.varId : v.varID;
        return String(varIdValue) === String(varId);
      });

      return {
        variableId: value,
        variableName: foundVar ? foundVar.varName : value,
        variableType: 'V'
      };
    }

    if (value.startsWith('tag_')) {
      const tagId = value.substring(4);
      const foundTag = tags.find(t => {
        const tagIdValue = t.tagId !== undefined ? t.tagId : t.tagID;
        return String(tagIdValue) === String(tagId);
      });

      return {
        variableId: value,
        variableName: foundTag ? foundTag.tagName : value,
        variableType: 'T'
      };
    }

    return {
      variableId: value,
      variableName: value,
      variableType: 'constant'
    };
  };

  // Initialize data when component mounts or when data prop changes
  useEffect(() => {
    if (!data) return;

    setOperation(data.operation || 'concat');

    const newMappings = {
      input1: parseVariableString(data.selectedVariable1 || data.input1, data.inputType1 || data.selectInputType),
      input2: parseVariableString(data.selectedVariable2 || data.input2, data.inputType2 || data.selectInputType2),
      output: parseVariableString(data.selectedVariable3 || data.output, data.inputType3)
    };

    console.log('Initialized MetinselIslemler Mappings:', newMappings);
    setParameterMappings(newMappings);
  }, [data, vars, tags, localVars]);

  const getColorForType = (type) => {
    switch(type) {
      case 'V': return '#fff3e0';
      case 'T': return '#edbc45';
      case 'LV': return '#f3e5f5';
      case 'constant': return '#e0e0e0';
      default: return '#f5f5f5';
    }
  };

  const handleVariableSelect = (event, variable) => {
    if (!focusedInput || !variable) return;

    console.log('Selected Variable:', variable);

    let variableId;
    let variableName;
    const variableType = variable.type;

    switch (variableType) {
      case 'LV':
        variableId = `lv_${variable.name}`;
        variableName = variable.name;
        break;
      case 'V':
        const varIdValue = variable.varID || variable.varId || variable.id;
        variableId = `var_${varIdValue}`;
        variableName = variable.varName || variable.name;
        break;
      case 'T':
        const tagIdValue = variable.tagID || variable.tagId || variable.id;
        variableId = `tag_${tagIdValue}`;
        variableName = variable.tagName || variable.name;
        break;
      case 'constant':
        variableId = variable.name;
        variableName = variable.name;
        break;
      default:
        variableId = variable.id;
        variableName = variable.name;
    }

    setParameterMappings(prev => ({
      ...prev,
      [focusedInput]: {
        variableId,
        variableName,
        variableType
      }
    }));
  };

  const InputCard = ({ label, inputKey, mapping, required = true }) => {
    const isFocused = focusedInput === inputKey;

    return (
      <Card
        onClick={() => setFocusedInput(inputKey)}
        sx={{
          cursor: 'pointer',
          border: isFocused ? '2px solid #6a1b9a' : '1px solid #ddd',
          backgroundColor: isFocused ? '#f3e5f5' : 'white',
          mb: 1
        }}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="body2" fontWeight={600} mb={0.5} color="text.secondary">
            {label}{required && ' *'}
          </Typography>

          {mapping?.variableName ? (
            <Box sx={{
              p: 1,
              backgroundColor: getColorForType(mapping.variableType),
              borderRadius: 1,
              fontSize: '0.9rem'
            }}>
              {mapping.variableName}
            </Box>
          ) : (
            <Typography variant="body2" color="text.disabled" fontStyle="italic">
              {required ? 'Değişken seçilmedi (zorunlu)' : 'Değişken seçilmedi (opsiyonel)'}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  // Operation options matching the Axis original
  const operationOptions = [
    { value: 'concat', label: 'Concat - Birleştir' },
    { value: 'split', label: 'Split - Ayır' },
    { value: 'replace', label: 'Replace - Değiştir' },
    { value: 'substring', label: 'Substring - Alt String' },
    { value: 'uppercase', label: 'Uppercase - Büyük Harf' },
    { value: 'lowercase', label: 'Lowercase - Küçük Harf' },
    { value: 'length', label: 'Length - Uzunluk' }
  ];

  // Get operation name for display
  const getOperationName = (op) => {
    const found = operationOptions.find(o => o.value === op);
    return found ? found.label.split('-')[0].trim() : op;
  };

  // Check which inputs are required based on operation
  const requiresInput1 = ['concat', 'split', 'replace', 'substring', 'uppercase', 'lowercase', 'length'].includes(operation);
  const requiresInput2 = ['concat', 'split', 'replace', 'substring'].includes(operation);
  const requiresOutput = true; // Output is always required

  const handleSave = () => {
    const { input1, input2, output } = parameterMappings;

    // Validate required fields based on operation
    if (requiresInput1 && !input1) {
      dispatch(setToastMessage({
        type: 'error',
        message: 'Lütfen Girdi 1 değerini seçin.'
      }));
      return;
    }

    if (requiresInput2 && !input2) {
      dispatch(setToastMessage({
        type: 'error',
        message: 'Lütfen Girdi 2 değerini seçin.'
      }));
      return;
    }

    if (!output) {
      dispatch(setToastMessage({
        type: 'error',
        message: 'Lütfen çıktı değişkenini seçin.'
      }));
      return;
    }

    // Build title based on operation
    let title;
    const opName = getOperationName(operation);

    if (requiresInput2) {
      title = `${opName}(${input1.variableName}, ${input2.variableName}) = ${output.variableName}`;
    } else {
      title = `${opName}(${input1.variableName}) = ${output.variableName}`;
    }

    // Prepare ElementDetail array (for Axis compatibility)
    const elementDetailList = [];

    // Add operation name
    elementDetailList.push({
      elementDetailVariableType: 'Value',
      elementDetailVariableId: opName
    });

    // Add input1 if exists
    if (input1) {
      elementDetailList.push({
        elementDetailVariableType: input1.variableType === 'constant' ? 'Value' : input1.variableType,
        elementDetailVariableId: input1.variableType === 'constant' ? input1.variableName : input1.variableId
      });
    }

    // Add input2 if exists
    if (input2) {
      elementDetailList.push({
        elementDetailVariableType: input2.variableType === 'constant' ? 'Value' : input2.variableType,
        elementDetailVariableId: input2.variableType === 'constant' ? input2.variableName : input2.variableId
      });
    }

    // Add output
    if (output) {
      elementDetailList.push({
        elementDetailVariableType: output.variableType === 'constant' ? 'Value' : output.variableType,
        elementDetailVariableId: output.variableType === 'constant' ? output.variableName : output.variableId
      });
    }

    const nodeData = {
      operation,
      selectedVariable1: input1 ? input1.variableId : '',
      selectedVariable2: input2 ? input2.variableId : '',
      selectedVariable3: output ? output.variableId : '',
      inputType1: input1?.variableType === 'constant' ? 'text' : 'select',
      inputType2: input2?.variableType === 'constant' ? 'text' : 'select',
      inputType3: output?.variableType === 'constant' ? 'text' : 'select',
      // Backward compatibility with old property names
      input1: input1 ? input1.variableId : '',
      input2: input2 ? input2.variableId : '',
      output: output ? output.variableId : '',
      selectInputType: input1?.variableType === 'constant' ? 'text' : 'select',
      selectInputType2: input2?.variableType === 'constant' ? 'text' : 'select',
      // ElementDetail for Axis compatibility
      ElementDetail: elementDetailList,
      InputType: input1?.variableType === 'constant' ? 'text' : 'select',
      InputType2: input2?.variableType === 'constant' ? 'text' : 'select',
      label: 'MetinselIslemler',
      Title: title,
    };

    console.log('Saving MetinselIslemler Node Data:', nodeData);

    onChange(nodeData);
    dispatch(isModalOpen());
    dispatch(setToastMessage({
      type: 'success',
      message: 'Metinsel işlem kaydedildi.'
    }));
  };

  if (nodeType !== "MetinselIslemler" && nodeType !== "MetinIslemleri") return null;

  return (
    <PanelShell title="Metinsel İşlemler" onSave={handleSave}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: '100%', overflow: 'hidden' }}>
        {/* Variable Selector */}
        <Box>
          <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">
            Değişken / Sabit Seçimi
          </Typography>
          <VariableAutocomplete
            value={null}
            onChange={handleVariableSelect}
            placeholder="Tag, değişken veya sabit ara..."
            showConstants={true}
          />
          {focusedInput && (
            <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
              Seçilen alan: {focusedInput === 'input1' ? 'Girdi 1' : focusedInput === 'input2' ? 'Girdi 2' : 'Çıktı Değişkeni'}
            </Typography>
          )}
        </Box>

        {/* Operation Type */}
        <FormControl fullWidth size="small">
          <InputLabel>İşlem Tipi</InputLabel>
          <Select
            value={operation}
            label="İşlem Tipi"
            onChange={(e) => setOperation(e.target.value)}
          >
            {operationOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Operation description */}
        <Box sx={{ p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {operation === 'concat' && 'İki metni birleştirir'}
            {operation === 'split' && 'Metni belirtilen karaktere göre ayırır'}
            {operation === 'replace' && 'Metindeki belirli bir değeri değiştirir'}
            {operation === 'substring' && 'Metnin belirli bir kısmını alır'}
            {operation === 'uppercase' && 'Metni büyük harfe çevirir'}
            {operation === 'lowercase' && 'Metni küçük harfe çevirir'}
            {operation === 'length' && 'Metnin uzunluğunu döndürür'}
          </Typography>
        </Box>

        {/* Input Parameters */}
        <Box>
          {requiresInput1 && (
            <InputCard
              label="Girdi 1"
              inputKey="input1"
              mapping={parameterMappings.input1}
              required={true}
            />
          )}

          {requiresInput2 && (
            <InputCard
              label="Girdi 2"
              inputKey="input2"
              mapping={parameterMappings.input2}
              required={true}
            />
          )}

          <InputCard
            label="Çıktı Değişkeni"
            inputKey="output"
            mapping={parameterMappings.output}
            required={true}
          />
        </Box>
      </Box>
    </PanelShell>
  );
};

export default MetinselIslemlerPanel;
